import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Check, ArrowRight, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ConsultationDialog from "@/components/ConsultationDialog";
import SeriesNav from "@/components/SeriesNav";
import PageReader from "@/components/PageReader";
import { Reveal } from "@/lib/motion";
import { getServicePage } from "@/data/services";
import usePageSeo, { useBreadcrumbSchema, useFaqSchema, useServiceSchema } from "@/hooks/usePageSeo";
import { PAGE_SEO } from "@/data/pageSeo";

export default function ServicePage({ category }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [consultOpen, setConsultOpen] = useState(false);
  const entry = getServicePage(category, slug);
  const page = entry?.page;
  const seoKey = `/${category}/${slug}`;
  const title = page ? `${page.badge} | WebsiteTech.pro` : "Page not found — WebsiteTech.pro";
  // Smart description: prefer explicit metaDescription; else build from lede/tagline
  // and truncate at a word boundary (never mid-word).
  const rawDesc = page
    ? (page.metaDescription || page.lede || page.tagline || page.description || `${page.badge} — WebsiteTech.pro service page.`)
    : "The service page you're looking for doesn't exist. Browse all WebsiteTech services.";
  const description = (() => {
    const s = String(rawDesc).replace(/\s+/g, " ").trim();
    if (s.length <= 170) return s;
    const cut = s.slice(0, 170);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > 120 ? cut.slice(0, lastSpace) : cut).replace(/[,;:–—-]$/, "") + "…";
  })();
  // 404 state is marked noindex so crawlers never cache a soft-404.
  usePageSeo(page ? (PAGE_SEO[seoKey] || { title, description }) : { title, description, noindex: true });
  useBreadcrumbSchema(
    page
      ? [{ name: "Home", url: "/" }, { name: category.charAt(0).toUpperCase() + category.slice(1), url: `/${category}` }, { name: page.badge, url: seoKey }]
      : []
  );
  // Per-page Service + FAQPage schema (lead-gen pages carry the most benefit).
  useServiceSchema(page ? { name: page.badge, description: page.lede || page.tagline } : null);
  useFaqSchema(page?.faqs || []);

  useEffect(() => { window.scrollTo(0, 0); }, [slug, page]);

  if (!entry) {
    return (<><Navbar /><main className="pt-40 pb-40 text-center min-h-screen"><h1 className="font-heading text-4xl font-bold text-slate-900">Page not found</h1><Link to="/" className="text-brand mt-4 inline-block">Back home</Link></main><Footer /></>);
  }

  const { base, tagline, siblings, prev, next } = entry;

  const act = (action) => {
    if (action === "planner") navigate("/planner");
    else if (action === "ai") window.dispatchEvent(new Event("open-ai"));
    else if (action === "advisor") navigate("/growth-advisor");
    else if (action === "analyze") { navigate("/"); setTimeout(() => document.getElementById("analyzer")?.scrollIntoView({ behavior: "smooth" }), 400); }
    else setConsultOpen(true);
  };

  return (
    <>
      <Navbar />
      <main className="pt-24">
        <section className="relative overflow-hidden hero-bg border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="overline">{entry.catLabel} · {page.badge}</span>
              <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight mt-3 leading-[1.05]">{page.title}</h1>
              <p className="text-lg text-slate-500 mt-5 leading-relaxed">{page.lede}</p>
              <div className="flex flex-wrap gap-3 mt-8">
                {page.ctas.map((c, i) => (
                  <button key={c.label} onClick={() => act(c.action)} data-testid={`cta-${c.action}-${i}`} className={`rounded-xl px-6 py-3.5 flex items-center gap-2 text-sm ${i === 0 ? "btn-primary" : "btn-outline"}`}>
                    {c.label} <ArrowRight size={15} />
                  </button>
                ))}
              </div>
            </div>
            {page.image && (
              <div className="relative"><img src={page.image} alt={page.badge} className="w-full h-80 object-cover rounded-2xl soft-shadow-lg" /></div>
            )}
          </div>
        </section>

        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-page-reader-root>
          <PageReader title={page.title} />

          {page.intro.map((p, i) => <p key={i} className="text-lg text-slate-600 leading-relaxed mb-4">{p}</p>)}

          {page.sections.map((s, i) => (
            <Reveal key={i} className="mt-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-4">{s.heading}</h2>
              {s.paras?.map((p, j) => <p key={j} className="text-slate-600 text-lg leading-relaxed mb-4">{p}</p>)}
              {s.flow && (
                <div className="flex flex-wrap items-center gap-2 my-5">
                  {s.flow.split("\u2192").map((step, k) => (
                    <span key={k} className="text-sm font-medium bg-blue-50 text-brand rounded-lg px-3 py-1.5">{step.trim()}</span>
                  ))}
                </div>
              )}
              {s.list && (
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-4">
                  {s.list.map((it, j) => (
                    <li key={j} className="flex gap-2.5 text-slate-600"><Check size={16} className="text-green-600 mt-1 shrink-0" /> <span>{it}</span></li>
                  ))}
                </ul>
              )}
              {s.pairs && (
                <div className="mt-4 border border-slate-200 rounded-2xl overflow-hidden">
                  <div className="grid grid-cols-2 bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-400"><span className="px-4 py-2">Before</span><span className="px-4 py-2 border-l border-slate-200">After</span></div>
                  {s.pairs.map((p, j) => (
                    <div key={j} className="grid grid-cols-2 border-t border-slate-100 text-sm">
                      <span className="px-4 py-2.5 text-slate-400 line-through">{p[0]}</span>
                      <span className="px-4 py-2.5 border-l border-slate-100 font-semibold text-slate-800">{p[1]}</span>
                    </div>
                  ))}
                </div>
              )}
              {s.kpis && (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`kpi-grid-${i}`}>
                  {s.kpis.map((k, j) => (
                    <div key={j} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`kpi-card-${i}-${j}`}>
                      <div className="text-2xl md:text-3xl font-extrabold text-slate-900 leading-none">
                        {k.value}
                        {k.suffix && <span className="text-base text-brand ml-0.5">{k.suffix}</span>}
                      </div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mt-2">{k.label}</div>
                      {k.detail && <div className="text-xs text-slate-500 mt-1">{k.detail}</div>}
                    </div>
                  ))}
                </div>
              )}
              {s.caseStudy && (
                <div className="mt-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-6 md:p-8" data-testid={`case-study-${i}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${s.caseStudy.label === "Live" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/30" : s.caseStudy.label === "Pilot" ? "bg-amber-500/20 text-amber-300 border border-amber-400/30" : "bg-white/10 text-white/70 border border-white/20"}`}>
                        {s.caseStudy.label || "Illustrative"}
                      </span>
                      {s.caseStudy.client && (
                        <span className="text-xs text-white/70">{s.caseStudy.client}</span>
                      )}
                    </div>
                    {s.caseStudy.industry && (
                      <span className="text-[11px] text-white/50">{s.caseStudy.industry}</span>
                    )}
                  </div>
                  {s.caseStudy.headline && (
                    <h3 className="font-heading text-xl md:text-2xl font-bold mt-3 leading-tight">{s.caseStudy.headline}</h3>
                  )}
                  {s.caseStudy.summary && (
                    <p className="text-sm text-white/80 mt-2 leading-relaxed">{s.caseStudy.summary}</p>
                  )}
                  {s.caseStudy.metrics && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                      {s.caseStudy.metrics.map((m, j) => (
                        <div key={j} className="bg-white/5 border border-white/10 rounded-xl p-3">
                          <div className="text-xl md:text-2xl font-extrabold">{m.value}<span className="text-sm text-emerald-300 ml-0.5">{m.suffix || ""}</span></div>
                          <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold mt-1">{m.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {s.caseStudy.quote && (
                    <blockquote className="mt-5 pl-4 border-l-2 border-emerald-400/50 text-sm italic text-white/80">
                      "{s.caseStudy.quote}"
                      {s.caseStudy.attribution && (
                        <footer className="mt-2 text-[11px] not-italic text-white/50">— {s.caseStudy.attribution}</footer>
                      )}
                    </blockquote>
                  )}
                </div>
              )}
            </Reveal>
          ))}

          <div className="mt-14 flex flex-wrap gap-3 border-t border-slate-200 pt-8">
            {page.ctas.map((c, i) => (
              <button key={c.label} onClick={() => act(c.action)} className={`rounded-xl px-6 py-3.5 flex items-center gap-2 text-sm ${i === 0 ? "btn-primary" : "btn-outline"}`}>{c.label} <ArrowRight size={15} /></button>
            ))}
          </div>
        </article>

        {category === "ai" && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
            <Link to="/foodpulse" data-testid="foodpulse-showcase" className="block relative overflow-hidden rounded-3xl bg-[#062a1e] text-white p-8 md:p-10 group">
              <div className="absolute -right-10 -top-10 w-48 h-48 bg-[#16A34A]/25 blur-3xl rounded-full" />
              <div className="relative flex flex-col md:flex-row md:items-center gap-6 justify-between">
                <div className="max-w-xl">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-200 bg-white/5 border border-emerald-400/20 rounded-full px-3 py-1.5 mb-4">Live demo · built on WebsiteTech AI Core</span>
                  <h2 className="font-heading text-2xl md:text-3xl font-extrabold leading-tight">Meet FoodPulse — a vision AI you can try right now</h2>
                  <p className="text-emerald-50/80 mt-3">Snap a photo of any dish or ingredient label and get instant identification, nutrition, recipes and food-waste tips. This is the kind of custom AI assistant we build for your business.</p>
                </div>
                <span className="inline-flex items-center gap-2 bg-[#16A34A] group-hover:bg-[#15903f] transition-colors rounded-xl px-6 py-3.5 font-semibold whitespace-nowrap">Try FoodPulse <ArrowUpRight size={17} /></span>
              </div>
            </Link>
          </section>
        )}

        {siblings.length > 1 && (
          <SeriesNav base={base} tagline={tagline} siblings={siblings} currentSlug={slug} prev={prev} next={next} />
        )}

        {page.faqs?.length > 0 && (
          <section className="bg-white border-t border-slate-100 py-14" data-testid="service-faqs">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <span className="overline">Frequently asked</span>
              <h2 className="font-heading font-extrabold text-slate-900 text-3xl sm:text-4xl mt-2">
                {page.navLabel} — questions we hear every week.
              </h2>
              <div className="mt-6 divide-y divide-slate-200 border border-slate-200 rounded-2xl bg-white overflow-hidden">
                {page.faqs.map((f, i) => (
                  <details key={i} className="group" data-testid={`service-faq-${i}`}>
                    <summary className="flex items-start justify-between gap-4 px-5 py-4 cursor-pointer list-none hover:bg-slate-50">
                      <span className="font-semibold text-slate-900 text-sm">{f.question}</span>
                      <span className="shrink-0 text-brand text-xl font-bold group-open:rotate-45 transition-transform leading-none">+</span>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{f.answer}</div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {page.related?.length > 0 && (
          <section className="bg-slate-50 border-t border-slate-100 py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <span className="overline">Related {entry.catLabel} Services</span>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                {page.related.slice(0, 3).map((r) => {
                  const rp = getServicePage(category, r)?.page;
                  if (!rp) return null;
                  return (
                    <Link key={r} to={`${base}/${r}`} className="bg-white border border-slate-200 rounded-2xl p-6 card-shadow hover-lift group" data-testid={`related-${r}`}>
                      <span className="overline">{rp.badge}</span>
                      <h3 className="font-heading text-xl font-bold text-slate-900 mt-2 flex items-center justify-between">{rp.navLabel} <ArrowUpRight size={18} className="text-slate-300 group-hover:text-brand transition-colors" /></h3>
                      <p className="text-sm text-slate-500 mt-2 line-clamp-2">{rp.lede}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ConsultationDialog open={consultOpen} onOpenChange={setConsultOpen} context={{ service_interest: page.badge, source: `${entry.catLabel} / ${page.navLabel}` }} />
    </>
  );
}
